/**
 * `PollingScheduler` — orchestreert twee mutually-exclusive setTimeout-
 * recursive pollers (conversation-poll vs service-status-poll) met
 * adaptieve interval-keuze op basis van panel-state, tab-visibility en
 * recente activiteit.
 *
 * ## Waarom een aparte class
 *
 * De polling-strategie heeft genoeg eigen complexiteit (2 timers, 5
 * configurabele intervallen, panel/visibility/activity-input, immediate-
 * fetch-on-resume) om hem los te zetten van de controller. Dat maakt:
 *
 *   1. de controller dunner — die hoeft alleen `setPanelOpen(true)` te
 *      seinen i.p.v. timers handmatig te clearen+rescheduleen.
 *   2. de scheduling-logica unit-testbaar zonder LitElement, ChatState
 *      of een echte API. Vitest fake-timers + state-injectie volstaan.
 *
 * ## Geen kennis van app-state
 *
 * De scheduler kent géén `ChatState`. Hij neemt van de controller alleen:
 *   - polling-callbacks (functies die "doe een fetch + dispatch")
 *   - state-hints via setters (panel-open, visibility, activity-marker)
 *
 * Mutually-exclusive: max één van de twee pollers heeft tegelijk een
 * callback geset. De controller bepaalt dit via session-phase.
 *
 * ## Pause/resume bij visibility-change
 *
 * Als de tab in de achtergrond gaat, clearen we de timers maar laten de
 * callbacks ge-set. Op resume herstart-en we automatisch met een
 * immediate fetch — bezoeker ziet verse data zodra-ie terug is.
 */

export type PollCallback = () => void | Promise<void>;

export type PollingSchedulerConfig = {
  /** Conv-poll wanneer panel open is én er recent activiteit was. */
  pollIntervalMs?: number;
  /** Conv-poll wanneer panel dicht is OF gesprek "stil" staat. */
  adaptivePollIntervalMs?: number;
  /** Hoelang het stil moet zijn (ms) voordat adaptive ingaat. */
  adaptiveThresholdMs?: number;
  /** Status-poll wanneer panel open is. */
  statusPollIntervalMs?: number;
  /** Status-poll wanneer panel dicht is. */
  idleStatusPollIntervalMs?: number;
};

type ResolvedConfig = Required<PollingSchedulerConfig>;

const DEFAULTS: ResolvedConfig = {
  pollIntervalMs: 2000,
  adaptivePollIntervalMs: 6000,
  adaptiveThresholdMs: 60_000,
  statusPollIntervalMs: 5000,
  idleStatusPollIntervalMs: 30_000,
};

export class PollingScheduler {
  private readonly config: ResolvedConfig;

  private convTimer: ReturnType<typeof setTimeout> | null = null;
  private statusTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Callback = null betekent: deze poller is niet aangevraagd. Wordt op
   * stop ge-nullt en bij resume gebruikt om te bepalen welke pollers
   * her-gestart moeten worden.
   */
  private convCallback: PollCallback | null = null;
  private statusCallback: PollCallback | null = null;

  private panelOpen = false;
  private visible = true;
  private lastConvActivityAt = 0;

  constructor(config: PollingSchedulerConfig = {}) {
    this.config = { ...DEFAULTS, ...config };
  }

  // ────────────────────────── Lifecycle ──────────────────────────

  /**
   * Start conversation-polling. Voert callback meteen uit (bezoeker ziet
   * direct verse data) en schedulet daarna recursieve polls op een
   * panel/visibility/activity-aware interval.
   */
  startConvPolling(callback: PollCallback): void {
    this.stopConvPolling();
    this.convCallback = callback;
    this.lastConvActivityAt = Date.now();
    if (!this.visible) return; // wacht op visibility-resume
    void this.runConv();
    this.scheduleNextConvPoll();
  }

  stopConvPolling(): void {
    if (this.convTimer) {
      clearTimeout(this.convTimer);
      this.convTimer = null;
    }
    this.convCallback = null;
  }

  /**
   * Start service-status-polling. Idempotent — herhaalde calls met dezelfde
   * callback hebben geen effect (timer blijft draaien).
   */
  startStatusPolling(callback: PollCallback): void {
    if (this.statusTimer && this.statusCallback === callback) return;
    this.stopStatusPolling();
    this.statusCallback = callback;
    if (!this.visible) return;
    this.scheduleNextStatusPoll();
  }

  stopStatusPolling(): void {
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
      this.statusTimer = null;
    }
    this.statusCallback = null;
  }

  stopAll(): void {
    this.stopConvPolling();
    this.stopStatusPolling();
  }

  // ────────────────────────── State updates ──────────────────────────

  /**
   * Bezoeker opent/sluit het paneel. Op open: immediate fetch + reschedule
   * met de snellere interval. Op close: huidige timer doortikken, volgende
   * tick gebruikt vanzelf de tragere interval.
   */
  setPanelOpen(open: boolean): void {
    if (this.panelOpen === open) return;
    this.panelOpen = open;
    if (!open) return;
    if (this.convCallback) this.rescheduleConvNow();
    else if (this.statusCallback) this.rescheduleStatusNow();
  }

  /**
   * Tab-visibility change. Hidden = pause (timers stoppen, callbacks
   * blijven set). Visible = resume met immediate fetch.
   */
  setVisibility(visible: boolean): void {
    if (this.visible === visible) return;
    this.visible = visible;
    if (!visible) {
      // Pause: alleen timers stoppen, callback-refs houden.
      if (this.convTimer) {
        clearTimeout(this.convTimer);
        this.convTimer = null;
      }
      if (this.statusTimer) {
        clearTimeout(this.statusTimer);
        this.statusTimer = null;
      }
      return;
    }
    if (this.convCallback) this.rescheduleConvNow();
    else if (this.statusCallback) this.rescheduleStatusNow();
  }

  /**
   * Markeer dat er nét iets merkbaars in de conversatie is veranderd
   * (nieuwe message, agent-typing-flip, queue-position-shift). Reset de
   * adaptive-stilte-timer zodat de volgende poll snel gaat.
   */
  markConvActivity(): void {
    this.lastConvActivityAt = Date.now();
  }

  // ────────────────────────── Test-exposed pure berekeningen ──────────────────────────

  /**
   * Computed conv-poll-interval. Test-exposed zodat de scheduling-logica
   * unit-testbaar is zonder echte timers te laten lopen.
   */
  computeConvInterval(): number {
    const isQuiet =
      !this.panelOpen ||
      Date.now() - this.lastConvActivityAt > this.config.adaptiveThresholdMs;
    return isQuiet
      ? this.config.adaptivePollIntervalMs
      : this.config.pollIntervalMs;
  }

  computeStatusInterval(): number {
    return this.panelOpen
      ? this.config.statusPollIntervalMs
      : this.config.idleStatusPollIntervalMs;
  }

  // ────────────────────────── Internals ──────────────────────────

  private async runConv(): Promise<void> {
    const cb = this.convCallback;
    if (!cb) return;
    await cb();
  }

  private async runStatus(): Promise<void> {
    const cb = this.statusCallback;
    if (!cb) return;
    await cb();
  }

  private scheduleNextConvPoll(): void {
    if (!this.convCallback || !this.visible) return;
    this.convTimer = setTimeout(async () => {
      this.convTimer = null;
      if (!this.convCallback) return;
      await this.runConv();
      // Re-read after await — stopConvPolling could have nulled the callback
      // while the network request was in flight. TS narrowing from the early
      // return persists across await, so we must check explicitly here.
      if (this.convCallback !== null && this.visible) this.scheduleNextConvPoll();
    }, this.computeConvInterval());
  }

  private scheduleNextStatusPoll(): void {
    if (!this.statusCallback || !this.visible) return;
    this.statusTimer = setTimeout(async () => {
      this.statusTimer = null;
      if (!this.statusCallback) return;
      await this.runStatus();
      if (this.statusCallback !== null && this.visible) this.scheduleNextStatusPoll();
    }, this.computeStatusInterval());
  }

  private rescheduleConvNow(): void {
    if (!this.convCallback) return;
    if (this.convTimer) {
      clearTimeout(this.convTimer);
      this.convTimer = null;
    }
    if (!this.visible) return;
    void this.runConv();
    this.scheduleNextConvPoll();
  }

  private rescheduleStatusNow(): void {
    if (!this.statusCallback) return;
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
      this.statusTimer = null;
    }
    if (!this.visible) return;
    void this.runStatus();
    this.scheduleNextStatusPoll();
  }
}
